import React, { createContext, useContext, useState, useEffect } from 'react';

const UserProfileContext = createContext();

const DEFAULT_PROFILE = {
  name: '',
  dateOfBirth: '',
  timeOfBirth: '',
  placeOfBirth: '',
  zodiacSign: '',
  chineseZodiac: '',
  lifePathNumber: null,
  savedReadings: [],
  favorites: [],
  preferences: {
    notifications: false,
    dailyHoroscope: true,
  },
};

export function UserProfileProvider({ children }) {
  const [profile, setProfile] = useState(() => {
    const saved = localStorage.getItem('astroa2z_profile');
    return saved ? JSON.parse(saved) : DEFAULT_PROFILE;
  });

  useEffect(() => {
    localStorage.setItem('astroa2z_profile', JSON.stringify(profile));
  }, [profile]);

  const updateProfile = (updates) => {
    setProfile(prev => ({ ...prev, ...updates }));
  };

  const saveReading = (reading) => {
    const newReading = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...reading,
    };
    setProfile(prev => ({
      ...prev,
      savedReadings: [newReading, ...prev.savedReadings].slice(0, 50), // Keep last 50
    }));
    return newReading.id;
  };

  const deleteReading = (id) => {
    setProfile(prev => ({
      ...prev,
      savedReadings: prev.savedReadings.filter(r => r.id !== id),
    }));
  };

  const addFavorite = (item) => {
    const newFav = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...item,
    };
    setProfile(prev => ({
      ...prev,
      favorites: [newFav, ...prev.favorites].slice(0, 100),
    }));
    return newFav.id;
  };

  const removeFavorite = (id) => {
    setProfile(prev => ({
      ...prev,
      favorites: prev.favorites.filter(f => f.id !== id),
    }));
  };

  const isFavorite = (type, identifier) => {
    return profile.favorites.some(f => f.type === type && f.identifier === identifier);
  };

  const clearProfile = () => {
    setProfile(DEFAULT_PROFILE);
    localStorage.removeItem('astroa2z_profile');
  };

  const value = {
    profile,
    updateProfile,
    saveReading,
    deleteReading,
    addFavorite,
    removeFavorite,
    isFavorite,
    clearProfile,
    isProfileComplete: !!(profile.name && profile.dateOfBirth),
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile() {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
}
